import React, { useState, useEffect } from 'react';
import { Modal, Button, Descriptions, Input, Form, message } from 'antd';
import { CarOutlined, EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import api from '../../../services/api';

const ShippingInfoModal = ({ visible, onCancel, selectedOrder, onOrderUpdated }) => {
    const [form] = Form.useForm();
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [localOrderData, setLocalOrderData] = useState(null);

    // Initialize form data when selectedOrder changes
    useEffect(() => {
        if (selectedOrder) {
            setLocalOrderData(selectedOrder);
            const formValues = {
                shippingName: selectedOrder.shippingName || '',
                shippingAddress1: selectedOrder.shippingAddress1 || '',
                shippingAddress2: selectedOrder.shippingAddress2 || '',
                shippingCity: selectedOrder.shippingCity || '',
                shippingProvince: selectedOrder.shippingProvince || '',
                shippingZip: selectedOrder.shippingZip || '',
                shippingCountry: selectedOrder.shippingCountry || '',
                shippingCountryCode: selectedOrder.shippingCountryCode || '',
                shippingProvinceCode: selectedOrder.shippingProvinceCode || '',
                shippingPhone: selectedOrder.shippingPhone || '',
                email: selectedOrder.email || '',
                phone: selectedOrder.phone || ''
            };
            form.setFieldsValue(formValues);
        }
    }, [selectedOrder, form]);

    // Reset form when modal is closed
    useEffect(() => {
        if (!visible) {
            setIsEditMode(false);
            form.resetFields();
        }
    }, [visible, form]);

    const handleEdit = () => {
        if (localOrderData) {
            const formValues = {
                shippingName: localOrderData.shippingName || '',
                shippingAddress1: localOrderData.shippingAddress1 || '',
                shippingAddress2: localOrderData.shippingAddress2 || '',
                shippingCity: localOrderData.shippingCity || '',
                shippingProvince: localOrderData.shippingProvince || '',
                shippingZip: localOrderData.shippingZip || '',
                shippingCountry: localOrderData.shippingCountry || '',
                shippingCountryCode: localOrderData.shippingCountryCode || '',
                shippingProvinceCode: localOrderData.shippingProvinceCode || '',
                shippingPhone: localOrderData.shippingPhone || '',
                email: localOrderData.email || '',
                phone: localOrderData.phone || ''
            };
            form.setFieldsValue(formValues);
        }
        setIsEditMode(true);
    };

    const handleCancel = () => {
        if (isEditMode) {
            setIsEditMode(false);
            if (selectedOrder) {
                form.setFieldsValue({
                    shippingName: selectedOrder.shippingName || '',
                    shippingAddress1: selectedOrder.shippingAddress1 || '',
                    shippingAddress2: selectedOrder.shippingAddress2 || '',
                    shippingCity: selectedOrder.shippingCity || '',
                    shippingProvince: selectedOrder.shippingProvince || '',
                    shippingZip: selectedOrder.shippingZip || '',
                    shippingCountry: selectedOrder.shippingCountry || '',
                    shippingCountryCode: selectedOrder.shippingCountryCode || '',
                    shippingProvinceCode: selectedOrder.shippingProvinceCode || '',
                    shippingPhone: selectedOrder.shippingPhone || '',
                    email: selectedOrder.email || '',
                    phone: selectedOrder.phone || ''
                });
                setLocalOrderData(selectedOrder);
            }
        } else {
            onCancel();
        }
    };

    const handleModalClose = () => {
        onCancel();
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            
            const orderId = selectedOrder?.id;
            if (!orderId) {
                message.error('Không tìm thấy ID đơn hàng');
                return;
            }

            // Call API to update shipping info for Shopify order
            const response = await api.patch(`/shopify/orders/${orderId}/shipping-info`, values);

            if (response.data?.success) {
                message.success('Cập nhật thông tin giao hàng thành công');
                setIsEditMode(false);
                
                const updatedData = { ...localOrderData, ...values };
                setLocalOrderData(updatedData);
                
                if (onOrderUpdated) {
                    onOrderUpdated(updatedData);
                }
            } else {
                message.error(response.data?.message || 'Lỗi khi cập nhật thông tin giao hàng');
            }
        } catch (error) {
            console.error('Error updating shipping info:', error);
            const errorMessage = error.response?.data?.message || 'Lỗi khi cập nhật thông tin giao hàng';
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const renderField = (label, fieldName, placeholder) => {
        if (isEditMode) {
            return (
                <Form.Item 
                    name={fieldName}
                    style={{ margin: 0 }}
                    rules={[
                        {
                            max: 255,
                            message: `${label} không được vượt quá 255 ký tự`
                        }
                    ]}
                    initialValue={localOrderData?.[fieldName] || ''}
                >
                    <Input 
                        placeholder={placeholder}
                        maxLength={255}
                        defaultValue={localOrderData?.[fieldName] || ''}
                    />
                </Form.Item>
            );
        }
        
        const value = localOrderData?.[fieldName] || '';
        return value || '-';
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CarOutlined style={{ color: '#1890ff' }} />
                    <span>Shipping Information</span>
                    {!isEditMode && (
                        <Button 
                            type="text" 
                            size="small"
                            icon={<EditOutlined />}
                            onClick={handleEdit}
                            style={{ marginLeft: 'auto' }}
                        >
                            Edit
                        </Button>
                    )}
                </div>
            }
            open={visible}
            onCancel={handleModalClose}
            width={900}
            footer={
                isEditMode ? [
                    <Button 
                        key="cancel" 
                        onClick={handleCancel}
                        icon={<CloseOutlined />}
                    >
                        Cancel
                    </Button>,
                    <Button 
                        key="submit" 
                        type="primary" 
                        loading={loading}
                        onClick={handleSave}
                        icon={<SaveOutlined />}
                    >
                        Save
                    </Button>,
                ] : [
                    <Button key="close" onClick={handleModalClose}>
                        Close
                    </Button>
                ]
            }
        >
            <Form
                form={form}
                layout="vertical"
                style={{ marginTop: 16 }}
            >
                <Descriptions 
                    bordered 
                    column={2}
                    size="small"
                    labelStyle={{ fontWeight: 500, width: '30%' }}
                >
                    <Descriptions.Item label="Order Name" span={2}>
                        {localOrderData?.shopifyOrderName || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Customer Email" span={2}>
                        {renderField('Customer Email', 'email', 'Enter customer email')}
                    </Descriptions.Item>

                    <Descriptions.Item label="Customer Phone" span={2}>
                        {renderField('Customer Phone', 'phone', 'Enter customer phone')}
                    </Descriptions.Item>

                    <Descriptions.Item label="Recipient Name" span={2}>
                        {renderField('Recipient Name', 'shippingName', 'Enter recipient name')}
                    </Descriptions.Item>

                    <Descriptions.Item label="Shipping Phone" span={2}>
                        {renderField('Shipping Phone', 'shippingPhone', 'Enter shipping phone')}
                    </Descriptions.Item>

                    <Descriptions.Item label="Address Line 1" span={2}>
                        {renderField('Address Line 1', 'shippingAddress1', 'Enter address line 1')}
                    </Descriptions.Item>

                    <Descriptions.Item label="Address Line 2" span={2}>
                        {renderField('Address Line 2', 'shippingAddress2', 'Enter address line 2')}
                    </Descriptions.Item>

                    <Descriptions.Item label="City">
                        {renderField('City', 'shippingCity', 'Enter city')}
                    </Descriptions.Item>

                    <Descriptions.Item label="Province/State">
                        {renderField('Province/State', 'shippingProvince', 'Enter province or state')}
                    </Descriptions.Item>

                    <Descriptions.Item label="Province Code">
                        {renderField('Province Code', 'shippingProvinceCode', 'Enter province code')}
                    </Descriptions.Item>

                    <Descriptions.Item label="Postal Code">
                        {renderField('Postal Code', 'shippingZip', 'Enter postal code')}
                    </Descriptions.Item>

                    <Descriptions.Item label="Country">
                        {renderField('Country', 'shippingCountry', 'Enter country')}
                    </Descriptions.Item>

                    <Descriptions.Item label="Country Code">
                        {renderField('Country Code', 'shippingCountryCode', 'Enter country code')}
                    </Descriptions.Item>
                </Descriptions>
            </Form>
        </Modal>
    );
};

export default ShippingInfoModal;
